import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

/**
 * `development_plan_self_origin_enabled` の継承設定解決 (docs/design/v05-skill-registration-table-redesign-detail.md §4-4-3)。
 * mode:'self' かつ `development_plan:manage` を持たないユーザーの書込み可否判定にのみ使う
 * (§2-6-3: `assertEmployeeInScope` が実質的な唯一の書込みゲートであり、この解決結果は
 * `origin==='self'` のリクエストにのみ効くサーバー側判定であることに注意)。
 *
 * `GET /inheritable-settings/:key/resolve?node_id=` は既存の汎用継承設定解決エンドポイント
 * (routes/inheritable-settings.ts) をそのまま使う。新規APIは不要。
 */
type ResolveResponse = { value: unknown };

export function useDevelopmentPlanSelfOriginResolve(nodeId: string | null, enabled: boolean) {
  const query = useQuery<ResolveResponse>({
    queryKey: ['inheritable-settings', 'development_plan_self_origin_enabled', 'resolve', nodeId],
    queryFn: () => apiFetch<ResolveResponse>(
      `/inheritable-settings/development_plan_self_origin_enabled/resolve?node_id=${encodeURIComponent(nodeId as string)}`,
    ),
    enabled: enabled && !!nodeId,
    retry: false,
  });
  return {
    enabled: query.data?.value === true,
    // §4-4-3: 解決前 (isLoading) の間は呼び出し側が canManageDevelopmentPlan=false に固定する
    // (解決前に一瞬編集可能に見えてから読み取り専用に戻る「フラッシュ」を避けるため)。
    isLoading: (enabled && !!nodeId) && query.isLoading,
    isError: query.isError,
  };
}
